import { type Product, type UserLimit } from "~/api/products/types";
import type { BasketStore } from "~/providers/store/basket";
import { type BasketItem } from "~/stores/kiosk-session/types";

import { type UserLimitClient } from "./types";

type CalculateUserLimitsArgs = {
  /** All user limits for the authenticated employee */
  userLimits: UserLimit[];
  /** The basket products */
  basket: BasketStore["products"] | BasketItem[];
  /** All fetched products from the API */
  products: Product[];
};

/**
 * Calculates the remaining buyable products for an authenticated employee.
 *
 * It is used for the following reasons:
 * - disable incrementing the quantity for a product
 * - disable a product to be added to the basket
 *
 * @returns the remaining buyable products for the authenticated employee
 */
export function calculateUserLimits({
  userLimits,
  basket,
  products
}: CalculateUserLimitsArgs): UserLimitClient[] {
  if (!userLimits.length) return [];

  return userLimits.flatMap<UserLimitClient>((userLimit) => {
    const productIds = products
      .filter((product) => product.userLimitId === userLimit.id)
      .map((product) => product.id);

    if (!productIds.length) {
      return [];
    }

    const quantityInBasket = basket.reduce((acc, item) => {
      if (productIds.includes(item.productId)) {
        return acc + ("count" in item ? item.count : item.quantity);
      }

      return acc;
    }, 0);

    if (!quantityInBasket) {
      return {
        id: userLimit.id,
        remaining: userLimit.remaining,
        isLimitOver: userLimit.remaining <= 0
      };
    }

    const remaining = userLimit.remaining - quantityInBasket;

    return {
      id: userLimit.id,
      isLimitOver: remaining <= 0,
      remaining: Math.max(0, remaining)
    };
  });
}
